/* Hook that handles the logical of the Import page, including the
   steps, validating files, and so on. */

import { useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { useIntl } from "react-intl";

import {
  ExcelImport,
  Mode,
  excelImportParseFile,
  excelImportUploadFile,
  excelImportUploadZipfile,
} from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { zipFileEntries } from "@nens/shared/untestable-utils";
import { flatten, makeUnique, removeNullUndefined } from "@nens/shared-utils";

const STEP_CHOOSE_MODE = 0;
const STEP_UPLOAD = 1;
const STEP_VALIDATION = 2;
const STEP_IMPORT = 3;
const STEP_SUCCESS = 4;

interface ImportState {
  mode: Mode | null;
  excelFile: File | null;
  zipFile: File | null;
  zipFileEntries: string[];
  activeStep: number /* 0-based */;
  fileErrors: string[];
  validationErrors: string[];
  uploadErrors: string[];
  excelImport: ExcelImport | null;
}

const EMPTY_IMPORT_STATE: ImportState = {
  mode: null,
  excelFile: null,
  zipFile: null,
  zipFileEntries: [],
  activeStep: STEP_CHOOSE_MODE,
  fileErrors: [],
  validationErrors: [],
  uploadErrors: [],
  excelImport: null,
};

const isExcelFile = (file: File) => file.name.toLowerCase().endsWith(".xlsx");
const isZipFile = (file: File) => file.name.toLowerCase().endsWith(".zip");

export function useExcelImportPageHook() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { apiFetchFunction, tenantId } = useContext(LdoAuthContext);
  const [importState, setImportState] = useState<ImportState>(EMPTY_IMPORT_STATE);

  function setMode(mode: Mode) {
    setImportState({
      ...EMPTY_IMPORT_STATE,
      mode,
      activeStep: STEP_UPLOAD,
    });
  }

  function setValidationErrors(errors: string[]) {
    setImportState((importState) => ({
      ...importState,
      validationErrors: errors,
      uploadErrors: [],
    }));
  }

  function nextStep() {
    setActiveStep(importState.activeStep + 1);
  }

  function startOver() {
    setImportState(EMPTY_IMPORT_STATE);
  }

  function latestValidStep() {
    if (importState.mode === null) {
      return STEP_CHOOSE_MODE;
    }
    if (
      importState.excelFile === null ||
      (importState.mode === "create" && importState.zipFile === null)
    ) {
      return STEP_UPLOAD;
    }
    return STEP_SUCCESS;
  }

  function readyForStep(step: number) {
    return step <= latestValidStep();
  }

  const removeFile = (file: File) => {
    if (isExcelFile(file)) {
      setImportState((importState) => ({ ...importState, excelFile: null }));
    } else if (isZipFile(file)) {
      setImportState((importState) => ({ ...importState, zipFile: null }));
    }
  };

  const setActiveStep = (step: number) =>
    setImportState((importState) => ({
      ...importState,
      activeStep: step <= latestValidStep() ? step : importState.activeStep,
    }));

  const addFiles = (newFiles: File[]) => {
    setImportState((importState) => ({ ...importState, fileErrors: [] }));
    newFiles.forEach(async (file) => {
      if (isExcelFile(file)) {
        setImportState((importState) =>
          importState.excelFile === null ? { ...importState, excelFile: file } : importState
        );
      } else if (isZipFile(file) && importState.mode === "create") {
        try {
          const entries = await zipFileEntries(file);
          if (entries.length > 0) {
            setImportState((importState) => ({
              ...importState,
              zipFile: file,
              zipFileEntries: makeUnique(entries.map((entry) => entry.filename)),
            }));
          } else {
            setImportState((importState) => ({
              ...importState,
              fileErrors: [
                ...importState.fileErrors,
                intl.formatMessage(
                  {
                    id: "error.validation.zip_empty",
                    defaultMessage: "ZIP file {filename} had no files in it.",
                  },
                  { filename: file.name }
                ),
              ],
            }));
          }
        } catch (e) {
          setImportState((importState) => ({
            ...importState,
            fileErrors: [
              ...importState.fileErrors,
              intl.formatMessage(
                {
                  id: "error.validation.zip_corrupt",
                  defaultMessage: "Could not open ZIP file {filename}.",
                },
                { filename: file.name }
              ),
            ],
          }));
        }
      }
    });
  };

  // The actual validation and processing.
  const processFiles = async () => {
    const { mode, excelFile, zipFile } = importState;
    if (mode === null || excelFile === null || (mode === "create" && zipFile === null)) return;

    setActiveStep(STEP_VALIDATION);

    const parseResponse = await excelImportParseFile(apiFetchFunction, mode, excelFile);

    if (parseResponse.items.length === 0) {
      setValidationErrors([
        intl.formatMessage({
          id: "error.validation.excel_empty",
          defaultMessage: "No scenarios found in Excel file.",
        }),
      ]);
      return;
    } else {
      const validationErrors: string[] = [];
      parseResponse.items.forEach((item, idx) => {
        item.errors.forEach((error) => {
          const fields = `"${error.loc[0]}"`;
          const inputs = `"${item.input[error.loc[0]] ?? ""}"`;
          validationErrors.push(
            intl.formatMessage(
              {
                id: "error.validation.field_error",
                defaultMessage: `Scenario {number}, field {field}: found value(s) {inputs}, error was "{error}".`,
              },
              {
                number: idx + 1,
                field: fields,
                inputs,
                error: error.msg,
              }
            )
          );
        });
      });
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        return;
      }
    }

    // Check entries against files in the parseResponse
    const expectedFiles = makeUnique(
      flatten(parseResponse.items.map((item) => (item.files ?? []).map((file) => file.path)))
    );
    const notFoundFiles = expectedFiles.filter(
      (filename) => importState.zipFileEntries.indexOf(filename) === -1
    );
    if (notFoundFiles.length > 0) {
      setValidationErrors(
        notFoundFiles.map((filename) =>
          intl.formatMessage(
            {
              id: "error.validation.file_missing_in_zip",
              defaultMessage: `Expected to find file with name "{filename}" in ZIP file but it was not found.`,
            },
            { filename }
          )
        )
      );
      return;
    }

    // Actually upload
    setActiveStep(STEP_IMPORT);
    const excelImport = await excelImportUploadFile(apiFetchFunction, mode, excelFile);
    queryClient.invalidateQueries({
      queryKey: [tenantId, "ldo-api-v1", "scenarios"],
      refetchType: "all",
    });
    queryClient.invalidateQueries({
      queryKey: [tenantId, "ldo-api-v1", "excel-imports"],
      refetchType: "all",
    });

    if (mode === "create" && zipFile !== null) {
      // Also upload the zip file.
      try {
        await excelImportUploadZipfile(apiFetchFunction, excelImport.id, zipFile);
      } catch (e) {
        console.error("Upload error:", e);
      }
    }

    setImportState((importState) => ({
      ...importState,
      excelImport,
      activeStep: STEP_SUCCESS,
    }));
  };

  const files = removeNullUndefined([importState.excelFile, importState.zipFile]);

  return {
    mode: importState.mode,
    setMode,
    activeStep: importState.activeStep,
    readyForStep,
    nextStep,
    addFiles,
    removeFile,
    processFiles,
    files,
    startOver,
    validationErrors: importState.validationErrors,
    excelImport: importState.excelImport,
    STEP_CHOOSE_MODE,
    STEP_UPLOAD,
    STEP_VALIDATION,
    STEP_IMPORT,
    STEP_SUCCESS,
  };
}
